import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";
import { 
  LayoutDashboard, 
  TrendingUp, 
  FileText, 
  Clock, 
  Archive, 
  Search,
  LogOut,
  Zap,
  Sparkles,
  Globe,
  ShoppingBag
} from "lucide-react";

export const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Erfolgreich abgemeldet",
        description: "Du wurdest abgemeldet",
      });
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Fehler beim Abmelden",
        description: error.message,
        variant: "destructive",
      });
    }
  };
  
  const adminRoutes = [
    { 
      path: "/admin/dashboard", 
      label: "Dashboard", 
      shortLabel: "Home",
      icon: LayoutDashboard 
    },
    { 
      path: "/admin/trends", 
      label: "Trends", 
      shortLabel: "Trends",
      icon: TrendingUp 
    },
    { 
      path: "/admin/generator", 
      label: "Generator", 
      shortLabel: "Gen",
      icon: FileText 
    },
    { 
      path: "/admin/seo", 
      label: "SEO Analyzer", 
      shortLabel: "SEO",
      icon: Search 
    },
    { 
      path: "/admin/seo-pages", 
      label: "SEO Pages", 
      shortLabel: "Pages",
      icon: Globe 
    },
    { 
      path: "/admin/product-trends", 
      label: "Produkt-Trends", 
      shortLabel: "Produkte",
      icon: ShoppingBag 
    },
    { 
      path: "/admin/jobs", 
      label: "Jobs", 
      shortLabel: "Jobs",
      icon: Clock 
    },
    { 
      path: "/admin/backups", 
      label: "Backups", 
      shortLabel: "Save",
      icon: Archive 
    },
    { 
      path: "/admin/optimizer", 
      label: "Optimizer", 
      shortLabel: "Opt",
      icon: Sparkles 
    },
    { 
      path: "/admin/apis", 
      label: "APIs", 
      shortLabel: "APIs",
      icon: Zap 
    },
  ];

  // Find current tab - prioritize exact match, fallback to startsWith for nested routes
  const currentTab = adminRoutes.find(route => location.pathname === route.path)?.path 
    || adminRoutes
        .slice()
        .sort((a, b) => b.path.length - a.path.length)
        .find(route => location.pathname.startsWith(route.path))?.path 
    || "/admin/dashboard";

  return (
    <AdminProtectedRoute>
      <div className="min-h-screen">
      {/* Admin Navigation */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-16 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <Tabs value={currentTab} onValueChange={(value) => navigate(value)} className="flex-1">
              <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto gap-2 bg-transparent">
              {adminRoutes.map((route) => {
                const Icon = route.icon;
                return (
                  <TabsTrigger
                    key={route.path}
                    value={route.path}
                    className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <Icon className="h-4 w-4" />
                    {/* Full label on larger screens */}
                    <span className="hidden md:inline">{route.label}</span>
                    {/* Short label on small screens */}
                    <span className="md:hidden">{route.shortLabel}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="flex items-center gap-2 flex-shrink-0"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Abmelden</span>
          </Button>
          </div>
        </div>
      </div>

      {/* Admin Page Content */}
      <Outlet />
    </div>
    </AdminProtectedRoute>
  );
};
